import useTranslation from '@/composables/useTranslation.js'
/**
 * A composable that handles preventing navigation warnings when navigating away from a page.
 * It provides a reactive way to track prevent navigation and shows appropriate warnings for:
 * - Browser tab/window close
 * - Browser back/forward navigation
 * - Vue Router navigation
 *
 * @param confirmMessage - Optional custom message to show in the confirmation dialog.
 *
 * Note: This custom message will only be shown in:
 * - Vue Router navigation (using window.confirm)
 * - Browser back/forward navigation (using window.confirm)
 *
 * It will NOT be shown for:
 * - Browser tab/window close (browser shows its own native dialog)
 * - Page refresh (browser shows its own native dialog)
 *
 * For these cases, the browser's default "Leave site?" message will be shown.
 * This is due to security restrictions in modern browsers that prevent customizing
 * the beforeunload event message. While we can trigger the beforeunload event,
 * browsers will always show their own standardized message to prevent phishing
 * attempts and maintain a consistent user experience.
 *
 * @returns {Object} An object containing:
 *   - shouldPreventNavigation: A ref that should be set to true when navigation should be prevented
 *
 * @example
 * const { shouldPreventNavigation } = usePreventNavigation()
 *
 * // Set to true when navigation should be prevented
 * setShouldPreventNavigation(true)
 *
 * // Set to false when navigation should not be prevented
 * setShouldPreventNavigation(false)
 */

const shouldPreventNavigation = ref(false)

export function usePreventNavigation(confirmMessage?: string) {
  const setShouldPreventNavigation = (value: boolean) => {
    shouldPreventNavigation.value = value
  }

  const enablePreventNavigationEvents = () => {
    if (!import.meta.client) {
      return
    }

    const router = useRouter()
    const tCommon = useTranslation('common')
    const defaultConfirmMessage = tCommon('beforeUnload.title')
    const dialogMessage = confirmMessage || defaultConfirmMessage

    // Handle browser's beforeunload event
    useEventListener(
      window,
      'beforeunload',
      (e: BeforeUnloadEvent) => {
        if (shouldPreventNavigation.value) {
          // Modern browsers ignore the message and show their own
          e.preventDefault()
          e.returnValue = dialogMessage
          return dialogMessage
        }
      },
      { passive: false },
    )

    // Handle browser back/forward navigation
    useEventListener(window, 'popstate', () => {
      if (shouldPreventNavigation.value) {
        const currentRoute = router.currentRoute.value
        const previousRoute = router?.options?.history?.state?.back || ''

        // Remove query params and trailing slashes from route paths
        const cleanupRoute = (path: string) =>
          path.replace(/\?.*$/, '').replace(/\/$/, '')

        // Only show alert if it's a different page (ignoring query params and trailing slashes)
        const currentPath = currentRoute.path
        const previousPath = cleanupRoute(previousRoute.toString())
        const isDifferentPage = previousRoute && currentPath !== previousPath

        if (isDifferentPage && !confirm(dialogMessage)) {
          // If user cancels, navigate back to current page
          void navigateTo(currentRoute.fullPath)
        }
      }
    })

    // Handle Vue Router navigation
    onBeforeRouteLeave((to, from, next) => {
      if (shouldPreventNavigation.value) {
        // Only show alert if it's a different page (ignoring query params)
        const isDifferentPage = to.name !== from.name || to.path !== from.path
        if (isDifferentPage) {
          if (confirm(dialogMessage)) {
            return next()
          }
          return next(false)
        }
      }

      next()
    })

    // Reset the prevent navigation flag when the component is unmounted
    onBeforeUnmount(() => {
      setShouldPreventNavigation(false)
    })
  }

  return {
    enablePreventNavigationEvents,
    setShouldPreventNavigation,
    shouldPreventNavigation: readonly(shouldPreventNavigation),
  }
}
